import React, { useEffect, useState } from "react";
import "./Timestamp.css";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";

// Regex to extract YouTube video ID from common URL forms (watch?v=, youtu.be/, embed/)
// Captured group 1 is the 11-character video id
export const YT_URL_REGEX = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i;

export default function Timestamp() {
  const [input, setInput] = useState("");
  const [validationError, setValidationError] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [videos, setVideos] = useState([]);
  const [pendingUrl, setPendingUrl] = useState("");
  const [validUrl, setValidUrl] = useState("");
  const [lastFetchedUrl, setLastFetchedUrl] = useState("");
  const [timestampsLoading, setTimestampsLoading] = useState(false);
  const [timestampsResult, setTimestampsResult] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY || "";

  const splitInput = (value) =>
    value
      .split(/[,\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const findInvalidUrls = (entries) =>
    entries.filter((url) => !YT_URL_REGEX.test(url));

  const handleInputChange = (event) => {
    const value = event.target.value;
    setInput(value);
    setError("");

    const entries = splitInput(value);
    if (entries.length === 0) {
      setValidationError("");
      setPendingUrl("");
      setValidUrl("");
      setVideos([]);
      setLastFetchedUrl("");
      return;
    }

    const invalid = findInvalidUrls(entries);
    if (invalid.length > 0) {
      const list = invalid.slice(0, 3).join(", ");
      const suffix = invalid.length > 3 ? ", ..." : "";
      setValidationError(
        `Link is not a valid YouTube video link:`
      );
      setPendingUrl("");
      setValidUrl("");
      setVideos([]);
      setLastFetchedUrl("");
    } else {
      setValidationError("");
      const normalized = entries[0].trim();
      setPendingUrl(normalized);
      setTimestampsResult(null);
      setShowModal(false);
    }
  };

  useEffect(() => {
    if (!pendingUrl || pendingUrl === lastFetchedUrl) {
      return;
    }
    if (!API_KEY) {
      setError("Missing YouTube API key (REACT_APP_YOUTUBE_API_KEY)");
      return;
    }

    const controller = new AbortController();
    const fetchDetails = async () => {
      setLoading(true);
      setError("");
      setVideos([]);
      try {
        const normalized = pendingUrl.trim();
        const match = normalized.match(YT_URL_REGEX);
        const videoId = match ? match[1] : null;
        if (!videoId) {
          setError("Unable to extract video ID from URL.");
          setValidUrl("");
          return;
        }

        const url = `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${API_KEY}`;
        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) {
          const body = await res.text();
          throw new Error(`YouTube API error: ${res.status} ${body}`);
        }
        const data = await res.json();
        if (!data.items || data.items.length === 0) {
          setError("No videos returned from YouTube API.");
          setValidUrl("");
          return;
        }

        const item = data.items[0];
        const snippet = item.snippet || {};
        const thumbs = snippet.thumbnails || {};
        const thumbUrl =
          (thumbs.maxres && thumbs.maxres.url) ||
          (thumbs.standard && thumbs.standard.url) ||
          (thumbs.high && thumbs.high.url) ||
          (thumbs.medium && thumbs.medium.url) ||
          (thumbs.default && thumbs.default.url) ||
          "";

        setVideos([
          {
            id: item.id,
            title: snippet.title || "Untitled",
            thumbnail: thumbUrl,
          },
        ]);
        setValidUrl(normalized);
        setLastFetchedUrl(normalized);
      } catch (err) {
        if (err.name === "AbortError") {
          return;
        }
        setError(err.message || String(err));
        setValidUrl("");
        setLastFetchedUrl("");
        setTimestampsResult(null);
        setShowModal(false);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
    return () => controller.abort();
  }, [pendingUrl, lastFetchedUrl, API_KEY]);

  const handleGenerateTimestamps = async () => {
    if (!validUrl) {
      console.warn("No valid URL available for timestamp generation.");
      return;
    }

    setError("");
    setValidationError("");
    setTimestampsLoading(true);
    try {
      const callable = httpsCallable(functions, "generate_timestamps");
      const payload = { url: validUrl };
      console.log("generate_timestamps payload:", payload);
      const { data } = await callable(payload);
      console.log("Bumpups timestamps response:", data);
      setTimestampsResult(data);
      setShowModal(true);
    } catch (callErr) {
      console.error("Failed to generate timestamps:", callErr);
      const message =
        (callErr?.message || callErr?.details || "").trim() ||
        "Failed to generate timestamps.";
      setError(message);
    } finally {
      setTimestampsLoading(false);
    }
  };

  let buttonLabel = "Generate Timestamps";
  let buttonDisabled = true;

  if (validationError) {
    buttonLabel = "Invalid YouTube Video Link";
    buttonDisabled = true;
  } else if (timestampsLoading) {
    buttonLabel = "Generating Timestamps...";
    buttonDisabled = true;
  } else if (loading) {
    buttonLabel = "Loading Preview...";
    buttonDisabled = true;
  } else if (validUrl) {
    buttonDisabled = false;
  }

  return (
    <div className="timestamp">
      <h2>AI YouTube Timestamps</h2>
      <p>
        Paste a YouTube video URL to preview the thumbnail and title automatically, then generate timestamps when you are ready.
      </p>

      <div className="input-box">
        <textarea
          value={input}
          onChange={handleInputChange}
          placeholder="Paste a YouTube video URL"
          rows={3}
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button
            onClick={handleGenerateTimestamps}
            disabled={buttonDisabled}
            className={`${validationError ? "btn-invalid" : ""} ${timestampsLoading ? "btn-hammer" : ""}`}
          >
            {timestampsLoading && <span className="hammer-icon" aria-hidden>🔨</span>}
            {buttonLabel}
          </button>
        </div>
      </div>

      {validationError && <div className="ts-error">{validationError}</div>}
      {error && <div className="ts-error">{error}</div>}

      {loading && (
        <div className="macbook-loader">
          <div className="macbook">
            <div className="macbook-screen">
              <div className="loading-wheel" />
            </div>
            <div className="macbook-base" />
          </div>
          <p className="loader-caption">Fetching preview...</p>
        </div>
      )}

      <div className="results">
        {videos.map(v => (
          <div key={v.id} className="video-card">
            <div className="thumbnail">
              {v.thumbnail ? (
                <img src={v.thumbnail} alt={v.title} />
              ) : (
                <div className="no-thumb">No thumbnail</div>
              )}
            </div>
            <div className="video-title">{v.title}</div>
          </div>
        ))}
      </div>

      {showModal && timestampsResult && (
        <div className="timestamps-modal">
          <div className="modal-backdrop" onClick={() => setShowModal(false)} />
          <div className="modal-content" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3>Timestamps</h3>
              <button
                className="close-btn"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {(timestampsResult.timestamps_list || []).map((entry, idx) => {
                const [time, ...rest] = (entry || "").split(" - ");
                const text = rest.join(" - ");
                return (
                  <div key={`${entry}-${idx}`} className="timestamp-row">
                    <span className="timestamp-time">{time}</span>
                    <span className="timestamp-text">{text}</span>
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <button
                className="copy-btn"
                onClick={async () => {
                  const list = timestampsResult.timestamps_list || [];
                  if (!list.length) {
                    return;
                  }
                  try {
                    await navigator.clipboard.writeText(list.join("\n"));
                  } catch (copyErr) {
                    console.error("Failed to copy timestamps:", copyErr);
                  }
                }}
              >
                Copy All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
